<script setup lang="ts">
  import { reactive, watch, nextTick, ref, withDefaults, computed } from 'vue';

  interface Props {
    value?: {
      code: string;
      name: string;
      description: string;
      isActive: boolean;
      isPercentage: boolean;
      value: number;
      maxDiscountAmount: number | null;
    };
    mode: 'create' | 'edit';
  }

  const props = withDefaults(defineProps<Props>(), {
    value: () => ({
      code: '',
      name: '',
      description: '',
      isActive: true,
      isPercentage: true,
      value: 0,
      maxDiscountAmount: null,
    }),
  });
  const emit = defineEmits(['input']);

  const valueInput = ref(null);

  // Local copy of data
  const localData = reactive({
    code: props.value.code,
    name: props.value.name,
    description: props.value.description,
    isActive: props.value.isActive,
    isPercentage: props.value.isPercentage,
    value: props.value.value,
    maxDiscountAmount: props.value.maxDiscountAmount,
  });

  // Watch prop changes and update local data
  watch(
    () => props.value,
    (newValue) => {
      localData.code = newValue.code;
      localData.name = newValue.name;
      localData.description = newValue.description;
      localData.isActive = newValue.isActive;
      localData.isPercentage = newValue.isPercentage;
      localData.value = newValue.value;
      localData.maxDiscountAmount = newValue.maxDiscountAmount;
    },
    { deep: true }
  );

  // Watch local data changes and emit to parent
  watch(
    localData,
    (newValue) => {
      emit('input', {
        ...newValue,
        maxDiscountAmount: newValue.maxDiscountAmount || null,
      });
    },
    { deep: true }
  );

  // Validation rules
  const codeRules = [
    (v: string) => !!v || 'El código es requerido',
    (v: string) =>
      (v && v.length >= 3) || 'El código debe tener al menos 3 caracteres',
    (v: string) =>
      (v && v.length <= 20) || 'El código debe tener máximo 20 caracteres',
    (v: string) => /^[A-Z0-9]+$/.test(v) || 'Solo letras mayúsculas y números',
  ];

  const nameRules = [
    (v: string) => !!v || 'El nombre es requerido',
    (v: string) =>
      (v && v.length <= 100) || 'El nombre debe tener máximo 100 caracteres',
  ];

  const descriptionRules = [
    (v: string) =>
      !v ||
      v.length <= 500 ||
      'La descripción debe tener máximo 500 caracteres',
  ];

  const valueRules = computed(() => {
    if (localData.isPercentage) {
      return [
        (v: number) => v > 0 || 'El porcentaje debe ser mayor a 0',
        (v: number) => v <= 100 || 'El porcentaje debe ser menor o igual a 100',
      ];
    } else {
      return [(v: number) => v > 0 || 'El monto debe ser mayor a 0'];
    }
  });

  const maxDiscountRules = [
    (v: number | null) => !v || v > 0 || 'El monto máximo debe ser mayor a 0',
  ];

  // Auto-uppercase code
  watch(
    () => localData.code,
    (newCode) => {
      if (newCode && newCode !== newCode.toUpperCase()) {
        localData.code = newCode.toUpperCase();
      }
    }
  );

  // Reset value when switching type
  watch(
    () => localData.isPercentage,
    async (isPercentage) => {
      localData.value = 0;
      if (!isPercentage) {
        localData.maxDiscountAmount = null;
      }
      await selectInputText();
    }
  );

  async function selectInputText() {
    await nextTick();
    if (valueInput.value) {
      const input = (valueInput.value as any).$el.querySelector('input');
      if (input) {
        input.focus();
        input.select();
      }
    }
  }

  const valueLabel = computed(() =>
    localData.isPercentage ? 'Porcentaje de descuento' : 'Monto fijo'
  );

  const valueSuffix = computed(() => (localData.isPercentage ? '%' : ''));
</script>

<template>
  <div class="v2-modal-coupon-general">
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="localData.code"
          label="Código del cupón"
          placeholder="VERANO20"
          :rules="codeRules"
          :disabled="mode === 'edit'"
          outlined
          dense
          counter="20"
        >
          <template v-slot:prepend-inner>
            <v-icon small>mdi-ticket-percent</v-icon>
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="12" md="6">
        <v-switch
          v-model="localData.isActive"
          label="Cupón activo"
          hide-details
          class="mt-1"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model="localData.name"
          label="Nombre del cupón"
          placeholder="Descuento de verano"
          :rules="nameRules"
          outlined
          dense
          counter="100"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-textarea
          v-model="localData.description"
          label="Descripción (opcional)"
          placeholder="Descripción del cupón para los clientes"
          :rules="descriptionRules"
          outlined
          dense
          rows="2"
          counter="500"
        />
      </v-col>
    </v-row>

    <v-divider class="my-4" />

    <v-row>
      <v-col cols="12">
        <div class="discount-type-toggle">
          <v-btn-toggle
            v-model="localData.isPercentage"
            mandatory
            color="primary"
            class="mb-4"
          >
            <v-btn :value="true">
              <v-icon left small>mdi-percent</v-icon>
              Porcentaje
            </v-btn>
            <v-btn :value="false">
              <v-icon left small>mdi-currency-usd</v-icon>
              Monto fijo
            </v-btn>
          </v-btn-toggle>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          ref="valueInput"
          v-model.number="localData.value"
          :label="valueLabel"
          :suffix="valueSuffix"
          :rules="valueRules"
          type="number"
          outlined
          dense
        />
      </v-col>

      <v-col cols="12" md="6" v-if="localData.isPercentage">
        <v-text-field
          v-model.number="localData.maxDiscountAmount"
          label="Descuento máximo (opcional)"
          prefix="$"
          :rules="maxDiscountRules"
          type="number"
          outlined
          dense
          hint="Límite máximo del descuento en monto"
          persistent-hint
        />
      </v-col>
    </v-row>

    <v-alert
      v-if="localData.isPercentage && localData.value > 0"
      type="info"
      outlined
      dense
      class="mt-2"
    >
      <div class="text-body-2">
        El cupón aplicará un <strong>{{ localData.value }}%</strong> de
        descuento
        <span v-if="localData.maxDiscountAmount">
          (máximo <strong>${{ localData.maxDiscountAmount }}</strong
          >)
        </span>
      </div>
    </v-alert>

    <v-alert
      v-else-if="!localData.isPercentage && localData.value > 0"
      type="info"
      outlined
      dense
      class="mt-2"
    >
      <div class="text-body-2">
        El cupón aplicará un descuento de
        <strong>${{ localData.value.toFixed(2) }}</strong>
      </div>
    </v-alert>
  </div>
</template>

<style scoped lang="scss">
  .v2-modal-coupon-general {
    .discount-type-toggle {
      ::v-deep .v-btn-toggle {
        @apply tw-w-full;

        .v-btn {
          @apply tw-flex-1;
        }
      }
    }
  }
</style>
